import { Button } from "@/components/ui";
import { useAllProducts, useCategories } from "@/features/products/api";
import { useSettings } from "@/features/settings/api";
import { useCurrentShift } from "@/features/shifts/api";
import { notify } from "@/lib/notify";
import { formatCurrency } from "@/lib/utils";
import { useCartStore, useUIStore } from "@/stores";
import type { AttributeOption, Product, ProductVariant } from "@/types";
import { ShoppingCart, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAutoApplyPromotions } from "../hooks";
import {
    CartPanel,
    CustomerLinkDialog,
    PaymentDialog,
    ProductCustomizationDialog,
    ProductGrid,
} from "../components";

export function POSPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isMobileCartOpen, setIsMobileCartOpen] = useState(false);

  const posExpanded = useUIStore((s) => s.posExpanded);
  const togglePosExpanded = useUIStore((s) => s.togglePosExpanded);

  const { data: products, isLoading: productsLoading } = useAllProducts({
    status: "active",
  });
  const { data: categories } = useCategories();
  const { data: settings } = useSettings();
  const { data: currentShift } = useCurrentShift();

  const {
    items: cartItems,
    customer,
    discountAmount,
    promotion: cartPromotion,
    notes: orderNotes,
    orderType,
    getSubtotal,
    getItemCount,
    addItem,
    updateItemQuantity,
    setItemComped,
    removeItem,
    clearCart,
    setCustomer,
    setPromotion,
    clearPromotion,
    setNotes: setOrderNotes,
    setOrderType,
  } = useCartStore();

  useAutoApplyPromotions({
    cartItems,
    subtotal: getSubtotal(),
    cartPromotion,
    discountAmount,
    setPromotion,
    clearPromotion,
  });

  const currencySymbol = settings?.currency_symbol || "$";
  const subtotal = getSubtotal();
  const total = Math.max(0, subtotal - discountAmount);

  const filteredProducts = useMemo(() => {
    const term = search.toLowerCase();

    return (products ?? []).filter((product) => {
      const matchesSearch =
        !term ||
        product.name.toLowerCase().includes(term) ||
        product.sku.toLowerCase().includes(term);
      const matchesCategory =
        !selectedCategoryId || product.categoryId === selectedCategoryId;

      return matchesSearch && matchesCategory;
    });
  }, [products, search, selectedCategoryId]);

  const handleProductClick = (product: Product) => {
    if (product.attributes?.length || (product.variants?.length ?? 0) > 1) {
      setSelectedProduct(product);

      return;
    }

    const defaultVariant =
      product.variants?.find((v) => v.isDefault) || product.variants?.[0];

    if (defaultVariant) {
      addItem(product, defaultVariant, 1, []);
    }
  };

  const handleCustomizeConfirm = (
    variant: ProductVariant,
    selectedOptions: AttributeOption[],
    quantity: number,
  ) => {
    if (!selectedProduct) {
      return;
    }

    addItem(selectedProduct, variant, quantity, selectedOptions);
    notify.success(`Added ${selectedProduct.name} to cart`);
    setSelectedProduct(null);
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      notify.error("Cart is empty");

      return;
    }

    if (!currentShift) {
      notify.error("Open a shift before taking orders");

      return;
    }

    setIsPaymentModalOpen(true);
  };

  const cartProps = {
    cartItems,
    customer,
    orderType,
    orderNotes,
    subtotal,
    discountAmount,
    promotion: cartPromotion,
    total,
    currencySymbol,
    getItemCount,
    setCustomer,
    setOrderType,
    setOrderNotes,
    updateItemQuantity,
    setItemComped,
    removeItem,
    clearCart,
  };

  return (
    <div className="flex h-[calc(100vh-3.5rem)] bg-background md:h-screen">
      <ProductGrid
        search={search}
        onSearchChange={setSearch}
        categories={categories}
        selectedCategoryId={selectedCategoryId}
        onCategoryChange={(id: string | null) => {
          if (id === selectedCategoryId) {
            setSelectedCategoryId(null);

            return;
          }

          setSelectedCategoryId(id)
        }}
        products={filteredProducts}
        isLoading={productsLoading}
        currencySymbol={currencySymbol}
        onProductClick={handleProductClick}
        isExpanded={posExpanded}
        onToggleExpanded={togglePosExpanded}
      />
      <aside className="hidden w-[340px] flex-col border-l bg-secondary/40 lg:flex">
        <CartPanel
          {...cartProps}
          onCheckout={handleCheckout}
          onCustomerClick={() => setIsCustomerModalOpen(true)}
        />
      </aside>
      <div className="fixed bottom-4 left-4 right-4 z-40 lg:hidden">
        <Button
          className="h-14 w-full text-base shadow-lg"
          onClick={() => setIsMobileCartOpen(true)}
        >
          <ShoppingCart className="mr-2 h-5 w-5" />
          View Cart ({getItemCount()}) — {formatCurrency(total, currencySymbol)}
        </Button>
      </div>
      {isMobileCartOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsMobileCartOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] animate-in slide-in-from-bottom rounded-t-2xl bg-background shadow-xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <span className="font-semibold">Your Order</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileCartOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex max-h-[calc(85vh-56px)] flex-col overflow-hidden">
              <CartPanel
                {...cartProps}
                onCheckout={() => {
                  setIsMobileCartOpen(false);
                  handleCheckout();
                }}
                onCustomerClick={() => {
                  setIsMobileCartOpen(false);
                  setIsCustomerModalOpen(true);
                }}
                isMobile
              />
            </div>
          </div>
        </div>
      )}

      {selectedProduct && (
        <ProductCustomizationDialog
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onConfirm={handleCustomizeConfirm}
          currencySymbol={currencySymbol}
        />
      )}

      <CustomerLinkDialog
        open={isCustomerModalOpen}
        onOpenChange={setIsCustomerModalOpen}
        onSelectCustomer={(c) => {
          setCustomer(c);
          setIsCustomerModalOpen(false);
        }}
      />
      <PaymentDialog
        open={isPaymentModalOpen}
        onOpenChange={setIsPaymentModalOpen}
        total={total}
        currencySymbol={currencySymbol}
        shiftId={currentShift?.id}
        loyaltyEnabled={settings?.loyalty_enabled === "true"}
        loyaltyRedemptionRate={parseInt(
          settings?.loyalty_redemption_rate || "100",
        )}
        loyaltyRedemptionValue={parseFloat(
          settings?.loyalty_redemption_value || "1",
        )}
        onSuccess={(orderId, isPayLater, isQueued) => {
          clearCart();
          setIsPaymentModalOpen(false);

          if (isQueued) {
            notify.success("Saved offline — will sync when reconnected");
            navigate("/orders/pending");

            return;
          }

          if (isPayLater) {
            notify.success("Order created - payment pending");
            navigate("/orders?tab=history");

            return;
          }

          notify.success("Order completed!");
          navigate(`/orders/${orderId}/invoice`);
        }}
      />
    </div>
  );
}
